import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Globe, Layers, PlayCircle, RefreshCw, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SOURCE_TYPE_META } from "@/components/ripeness/radarMeta";
import { SponsorPipelinesDialog } from "@/components/ripeness/SponsorPipelinesDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const ACTIVE = ["queued", "processing"];

function SourceRow({ s }) {
    const dot = !s.enabled ? "bg-slate-600" : !s.configured ? "bg-rose-400" : s.programs_error ? "bg-amber-400" : "bg-emerald-400";
    return (
        <div className="flex items-start gap-2 py-1 border-t border-slate-800/70 first:border-t-0" data-testid={`ext-source-${s.key}`}>
            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
            <div className="flex-1 min-w-0">
                <div className="text-slate-300">{s.label}</div>
                <div className="text-slate-500" data-testid={`ext-source-counts-${s.key}`}>
                    {!s.enabled ? "off" : `${s.programs_ok.toLocaleString()} ${s.key === "pipelines" ? "sponsor" : "program"}${s.programs_ok === 1 ? "" : "s"}${s.key === "pipelines" ? " read" : ""} · ${s.programs_error} failing · every ${s.ttl_hours >= 168 ? "week" : "day"}`}
                </div>
                {s.note ? <div className="text-amber-300">{s.note}</div> : null}
                {s.last_error ? <div className="text-rose-300 truncate" title={s.last_error}>last error: {s.last_error}</div> : null}
            </div>
        </div>
    );
}

function RunBox({ run }) {
    if (!run) return <div className="text-slate-500 italic" data-testid="ext-no-run">The one-time run has not been started.</div>;
    const p = run.progress || {};
    const doneN = Math.max(p.programs_scanned || 0, p.programs_done || 0);
    const pct = p.programs_total ? Math.min(100, (100 * doneN) / p.programs_total) : 0;
    const active = ACTIVE.includes(run.status);
    return (
        <div className="border border-slate-800 rounded p-2" data-testid="ext-run">
            <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${run.status === "done" ? "border-emerald-400/40 text-emerald-300" : run.status === "failed" ? "border-rose-400/40 text-rose-300" : active ? "border-sky-400/40 text-sky-300" : "border-slate-500/30 text-slate-400"}`} data-testid="ext-run-status">
                    {active ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}{run.status === "cancelled" ? "stopped" : run.status}
                </span>
                <span className="text-slate-500 font-mono truncate">{run.job_id}</span>
            </div>
            <div className="h-1.5 rounded bg-slate-800 overflow-hidden mt-2"><div className="h-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
            <div className="text-slate-500 mt-1" data-testid="ext-run-progress">
                {doneN.toLocaleString()} / {(p.programs_total || 0).toLocaleString()} programs ({pct.toFixed(1)}%) · {(p.tracks_evaluated || 0).toLocaleString()} tracks re-scored
                {run.results ? <> · <span className="text-emerald-300">{run.results.tracks_changed} changed</span></> : null}
            </div>
            <div className="text-slate-600 mt-0.5">{fmt(run.finished_at || run.started_at || run.submitted_at)}{run.cancelled_by ? ` · stopped by ${run.cancelled_by}` : ""}</div>
        </div>
    );
}

function DocCounts({ st }) {
    const docs = Object.entries(st.documents || {});
    return (
        <div className="text-slate-500" data-testid="ext-doc-counts">
            {docs.length ? docs.map(([k, n]) => `${n.toLocaleString()} ${SOURCE_TYPE_META[k]?.label || k}`).join(" · ") : "No documents cached yet"}
            {" · "}pipeline reads ${st.pipeline_ai?.cost_usd?.toFixed(2) ?? "0.00"}
        </div>
    );
}

/** SEC / 10-K / press wires / pipeline pages / CTIS — status + the one-time run (start / stop). */
export function ExternalSourcesPanel({ onChanged }) {
    const [open, setOpen] = useState(true);
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showPipes, setShowPipes] = useState(false);
    const running = ACTIVE.includes(st?.run?.status);

    const load = useCallback(async () => {
        try { setSt((await api.get("/v1/ripeness/external/status")).data); } catch (e) { /* admin-only panel */ }
    }, []);
    useEffect(() => {
        load();
        const id = setInterval(load, running ? 5000 : 60000);
        return () => clearInterval(id);
    }, [load, running]);
    useEffect(() => { if (st && !running && st.run?.status === "done") onChanged?.(); }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = useCallback(async () => {
        if (!window.confirm(`Re-check all ${st?.programs_total?.toLocaleString() || ""} programs with SEC filings, 10-Ks, press releases, sponsor pipeline pages and CTIS? It runs gradually in the background (about 10 programs at a time) and can be stopped at any time.`)) return;
        setBusy(true);
        try {
            const r = await api.post("/v1/ripeness/external/refresh", { reason: "one-time run from the Radar dashboard" });
            toast.success(r.data.forwarded ? `One-time run started on the hub (${r.data.processed_on}) — results sync back as it progresses` : "One-time run started");
            load();
        } catch (e) {
            toast.error(e?.response?.status === 409 ? `Not started: ${formatApiErrorDetail(e)}` : `Start failed: ${formatApiErrorDetail(e)}`);
        } finally { setBusy(false); }
    }, [st, load]);

    const stop = useCallback(async () => {
        try {
            const r = await api.post("/v1/ripeness/external/refresh/stop");
            toast.info((r.data.status === "stopping" ? "Stopping after the current program — results so far are kept" : "No run is active") + (r.data.forwarded ? ` (on the hub)` : ""));
            load();
        } catch (e) { toast.error(`Stop failed: ${formatApiErrorDetail(e)}`); }
    }, [load]);

    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="ext-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2" data-testid="ext-panel-toggle">
                <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Extra sources</span>
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {open && st ? (
                <div className="text-xs space-y-2">
                    <div>{st.sources.map((s) => <SourceRow key={s.key} s={s} />)}</div>
                    <DocCounts st={st} />
                    <RunBox run={st.run} />
                    {st.managed_here === false ? (
                        <div className="text-[11px] text-sky-300/80" data-testid="ext-hub-note">Fetched on the hub{st.hub ? ` (${st.hub})` : ""} — documents, sponsor pages and runs arrive here by Radar Sync; a run you start here is carried out there.</div>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={start} disabled={busy || running || !st.enabled.length}
                            className="inline-flex items-center gap-1 rounded bg-emerald-600/80 hover:bg-emerald-600 px-2 py-1 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={running ? "A run is active" : "Re-check every program with the extra sources"} data-testid="ext-run-start-btn">
                            <PlayCircle className="w-3 h-3" /> {st.run && !running ? "Run again" : "Start one-time run"}
                        </button>
                        {running ? (
                            <button onClick={stop} className="inline-flex items-center gap-1 rounded border border-rose-400/50 text-rose-200 hover:bg-rose-500/20 px-2 py-1 transition-colors" data-testid="ext-run-stop-btn">
                                <Square className="w-3 h-3" /> Stop
                            </button>
                        ) : null}
                        <button onClick={() => setShowPipes(true)} className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/40 px-2 py-1 text-slate-200 hover:bg-slate-800 transition-colors" data-testid="ext-pipelines-btn">
                            <Globe className="w-3 h-3" /> Sponsor pipelines
                            {Object.keys(st.sponsors || {}).length ? <span className="text-slate-500">{Object.values(st.sponsors).reduce((a, b) => a + b, 0)}</span> : null}
                        </button>
                    </div>
                </div>
            ) : null}
            <SponsorPipelinesDialog open={showPipes} onClose={() => { setShowPipes(false); load(); }} />
        </div>
    );
}
